import { getPermissions } from "../../utilis/permissionStorage";
import { usePermissions } from "../../utilis/PermissionsContext";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaAngleDown, FaBan, FaFileInvoice, FaHandHoldingUsd, FaMoneyBillWave, FaSearch, FaTrashAlt } from "react-icons/fa";
import { view_ApiManager } from "../ApiManager/ApiManager";
import {
  fetchApplicationInvoiceCounts,
  fetchCommissionInvoiceCounts,
  fetchInvoiceCatalog,
  applyInvoiceCatalog,
} from "../../utilis/invoiceWorkspaceApi";
import InvoiceFollowupModal from "../GenericComponents/InvoiceFollowupModal";
import InvoiceFollowupListBell from "../GenericComponents/InvoiceFollowupListBell";
import { fetchInvoiceFollowupSummariesByInvoices } from "../../utilis/invoiceFollowupApi";
import {
  CONTEXT_INVOICE_COMMISSION,
  hasInvoiceFollowupPermission,
  resolveInvoiceFollowupContext,
} from "../../utilis/followupConstants";
import moment from "moment";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import {
  getFinanceInvoiceApiMessage,
  isFinanceInvoiceApiSuccess,
  normalizeFinanceInvoiceList,
  updateFinanceInvoiceExpectedPayment,
  mergeExpectedPaymentPatch,
  buildCommissionHubEditInvoicePath,
  buildFinanceCreateInvoicePath,
  buildFinanceEditInvoicePath,
  buildFinanceInvoicesReturnUrl,
  readFinanceInvoiceListTabsFromLocation,
} from "../../utilis/invoiceCreateHelpers";
import {
  getInvoiceStatusLabel,
  INVOICE_STATUSES,
  normalizeInvoiceStatus,
} from "../../utilis/invoiceStatusHelpers";
import { resolveFinanceInvoiceEffectiveStatus } from "../../utilis/invoiceEditHelpers";
import {
  resolveOverdueDisplay,
} from "../../utilis/invoiceOverdueHelpers";
import {
  canAccessAwaitingApproval,
  canAccessFinanceInvoicesHub,
  canDeleteFinanceInvoice,
  canViewApplicationInvoices,
  canViewCommissionInvoices,
  resolveFinanceInvoicesModuleTab,
} from "../../utilis/invoicePermissionHelpers";
import { canQuickPayInvoice } from "../../utilis/invoiceListPaymentHelpers";
import { resolveInvoiceLatestPaymentDate } from "../../utilis/invoiceEditHelpers";
import {
  deleteCommissionInvoice,
  listAllCommissionInvoices,
  updateCommissionInvoiceExpectedPayment,
  voidCommissionInvoice,
  voidFinanceInvoice,
} from "../reports/commissions/commissionsApi";
import CommissionInvoiceExpectedPaymentCell from "../reports/commissions/detail/CommissionInvoiceExpectedPaymentCell";
import CommissionInvoiceExpectedPaymentEditor from "../reports/commissions/detail/CommissionInvoiceExpectedPaymentEditor";
import FinanceInvoiceQuickPayment from "./FinanceInvoiceQuickPayment";
import FinanceInvoiceApproveEmailModal from "./FinanceInvoiceApproveEmailModal";
import ClientInvoicesPrintModal from "../Clients/ClientInvoicesPrintModal";
import "../Clients/clients.css";
import "../reports/commissions/commissions-responsive.css";

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
  { id: "awaiting_payment", label: "Awaiting Payment" },
  { id: "awaiting_approval", label: "Awaiting Approval" },
  { id: "partial", label: "Partial" },
  { id: "paid", label: "Paid" },
  { id: "void", label: "Void" },
];

const EMAIL_ENABLED_STATUS_TABS = new Set([
  "draft",
  "awaiting_payment",
  "partial",
  "paid",
]);

const INVOICE_MODULE_TABS = [
  {
    id: "application",
    label: "Application invoices",
    subtitle: "Client application fees and finance invoices",
    icon: FaFileInvoice,
  },
  {
    id: "commission",
    label: "Commission invoices",
    subtitle: "Provider commission billing",
    icon: FaHandHoldingUsd,
  },
];

const EMPTY_FILTERS = {
  name: "",
  email: "",
  status: "",
  invoice_number: "",
};

const COMMISSION_INVOICE_TYPES = new Set([
  "commission",
  "bonus",
  "commission_bonus",
]);

function isCommissionModuleInvoice(invoice) {
  const type = String(invoice?.invoice_type || invoice?.invoiceType || "")
    .trim()
    .toLowerCase();
  const source = String(
    invoice?.invoice_source_module || invoice?.invoiceSourceModule || "",
  )
    .trim()
    .toLowerCase();
  return COMMISSION_INVOICE_TYPES.has(type) || source === "new_commission";
}

function resolveCommissionTypeBadge(invoice) {
  const type = String(invoice?.invoice_type || invoice?.invoiceType || "")
    .trim()
    .toLowerCase();
  if (type === "bonus") return "Bonus";
  if (type === "commission_bonus") return "Commission + Bonus";
  if (type === "commission" || isCommissionModuleInvoice(invoice)) {
    return "Commission";
  }
  return null;
}

const DEFAULT_PAGINATION = {
  currentPage: 1,
  lastPage: 1,
  perPage: 15,
  total: 0,
  from: 0,
  to: 0,
};

function buildFinanceInvoiceParams(
  filters = {},
  pagination = DEFAULT_PAGINATION,
) {
  const params = {
    page: pagination.currentPage || 1,
    per_page: pagination.perPage || 15,
  };

  Object.entries(filters).forEach(([key, value]) => {
    const trimmed = String(value || "").trim();
    if (trimmed) params[key] = trimmed;
  });

  params.invoice_type = "general";

  return params;
}

function buildCommissionInvoiceListParams(
  filters = {},
  pagination = DEFAULT_PAGINATION,
) {
  const params = {
    page: pagination.currentPage || 1,
    per_page: pagination.perPage || 15,
  };

  Object.entries(filters).forEach(([key, value]) => {
    const trimmed = String(value || "").trim();
    if (trimmed) params[key] = trimmed;
  });

  return params;
}

function resolveInvoiceListStatus(filters = {}, statusTab = "draft") {
  const filterStatus = String(filters.status || "").trim();
  if (filterStatus) {
    return filterStatus === "overdue" ? "awaiting_payment" : filterStatus;
  }
  if (statusTab === "all") {
    return "";
  }
  return statusTab || "draft";
}

function withInvoiceListStatus(filters = {}, statusTab = "draft") {
  return {
    ...filters,
    status: resolveInvoiceListStatus(filters, statusTab),
  };
}

function normalizeFinanceInvoicePagination(response) {
  const meta =
    response?.data?.pagination ||
    response?.data?.meta ||
    response?.data?.data?.pagination ||
    null;

  if (!meta || typeof meta !== "object") {
    return null;
  }

  return {
    currentPage: Number(meta.current_page ?? meta.currentPage ?? 1) || 1,
    lastPage: Number(meta.last_page ?? meta.lastPage ?? 1) || 1,
    perPage: Number(meta.per_page ?? meta.perPage ?? 15) || 15,
    total: Number(meta.total ?? 0) || 0,
    from: Number(meta.from ?? 0) || 0,
    to: Number(meta.to ?? 0) || 0,
  };
}

function resolveInvoiceId(invoice) {
  return invoice?.id || invoice?.invoiceid || invoice?.invoice_id || null;
}

function resolveInvoiceNumber(invoice) {
  return (
    invoice?.invoice_number ||
    invoice?.invoice_no ||
    invoice?.invoiceNo ||
    "—"
  );
}

function resolveClientId(invoice) {
  return (
    invoice?.client_id ||
    invoice?.clientId ||
    invoice?.client_snapshot?.id ||
    invoice?.client_snapshot?.client_id ||
    null
  );
}

function resolveClientName(invoice, fallback = "") {
  return (
    invoice?.client_snapshot?.full_name ||
    invoice?.client_snapshot?.name ||
    invoice?.student_name ||
    invoice?.studentName ||
    invoice?.client_name ||
    invoice?.clientName ||
    invoice?.client?.name ||
    fallback ||
    "—"
  );
}

function renderInvoiceContactCell(
  name,
  { linkTo = null, linkClassName = "", cellClassName = "cms-commission-invoices-table__contact-cell" } = {},
) {
  const label = String(name || "").trim() || "—";
  const content = linkTo ? (
    <Link className={linkClassName} to={linkTo} title={label}>
      {label}
    </Link>
  ) : (
    <span className="cms-commission-invoices-table__contact-name" title={label}>
      {label}
    </span>
  );

  return <td className={cellClassName}>{content}</td>;
}

function resolveProviderName(invoice) {
  return (
    invoice?.provider_name ||
    invoice?.providerName ||
    invoice?.provider_snapshot?.name ||
    invoice?.provider_snapshot?.provider_name ||
    invoice?.branding_snapshot?.name ||
    invoice?.client_snapshot?.full_name ||
    invoice?.client_snapshot?.name ||
    "—"
  );
}

function resolveProviderId(invoice) {
  return (
    invoice?.provider_id ??
    invoice?.providerId ??
    invoice?.provider_snapshot?.id ??
    null
  );
}

function resolveCommissionPlanId(invoice) {
  return (
    invoice?.commission_plan_id ??
    invoice?.commissionPlanId ??
    invoice?.new_commission_plan_id ??
    null
  );
}

function resolveListRowFollowupContext(invoice, isCommissionTab) {
  if (isCommissionTab) return CONTEXT_INVOICE_COMMISSION;
  return resolveInvoiceFollowupContext(invoice);
}

function readInvoiceFollowupPermissions() {
  return getPermissions();
}

function resolveBulkEmailGroupKey(invoice, isCommission) {
  if (isCommission) {
    const providerId = resolveProviderId(invoice);
    if (providerId != null && String(providerId).trim()) {
      return `provider:${String(providerId).trim()}`;
    }
    const providerName = String(resolveProviderName(invoice) || "")
      .trim()
      .toLowerCase();
    return providerName ? `provider-name:${providerName}` : "";
  }

  return String(resolveClientId(invoice) || "").trim();
}

function resolveClientEmail(invoice) {
  return (
    invoice?.client_snapshot?.email ||
    invoice?.client_snapshot?.client_email ||
    invoice?.client_email ||
    invoice?.clientemail ||
    ""
  );
}

function resolveInvoiceStatus(invoice) {
  return resolveFinanceInvoiceEffectiveStatus(invoice, {
    mapOverdueTab: true,
  });
}

function resolveInvoiceStatusDisplay(invoice) {
  return resolveInvoiceStatus(invoice);
}

function formatMoney(value, currency = "AUD") {
  const amount = Number(value) || 0;
  return `${currency} ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function resolveInvoiceAmount(invoice) {
  const gross = Number(invoice?.gross_total ?? invoice?.grossTotal);
  if (Number.isFinite(gross) && gross > 0) return gross;

  const subtotal = Number(invoice?.subtotal);
  const discount = resolveInvoiceDiscount(invoice);
  if (Number.isFinite(subtotal) && subtotal > 0) {
    return discount > 0 ? subtotal + discount : subtotal;
  }

  const total = Number(invoice?.total_amount ?? invoice?.totalAmount);
  if (Number.isFinite(total) && total > 0) {
    return discount > 0 ? total + discount : total;
  }

  return Number(invoice?.amount) || 0;
}

function resolveInvoiceDiscount(invoice) {
  return (
    Number(
      invoice?.discount_total ??
        invoice?.discountTotal ??
        invoice?.discount_amount ??
        invoice?.discountAmount,
    ) || 0
  );
}

function resolveInvoiceTotal(invoice) {
  const explicit = Number(invoice?.total_amount ?? invoice?.totalAmount);
  if (Number.isFinite(explicit) && explicit >= 0) return explicit;

  return Math.max(
    0,
    resolveInvoiceAmount(invoice) - resolveInvoiceDiscount(invoice),
  );
}

/** Amount column: original invoice total. A discount must not reduce it. */
function resolveInvoiceTableAmount(invoice) {
  if (isCommissionModuleInvoice(invoice)) {
    return resolveInvoiceTotal(invoice);
  }

  const discount = resolveInvoiceDiscount(invoice);
  const net = Number(invoice?.total_amount ?? invoice?.totalAmount);
  if (discount > 0 && Number.isFinite(net)) {
    return net + discount;
  }

  return resolveInvoiceTotal(invoice);
}

function resolveExpectedPaymentSaveHandler(invoice) {
  return isCommissionModuleInvoice(invoice)
    ? updateCommissionInvoiceExpectedPayment
    : updateFinanceInvoiceExpectedPayment;
}

function toExpectedPaymentInvoiceShape(invoice) {
  return {
    expectedPaymentDate:
      invoice?.expected_payment_date ||
      invoice?.expectedPaymentDate ||
      null,
    expectedPaymentNote:
      invoice?.expected_payment_note ||
      invoice?.expectedPaymentNote ||
      "",
  };
}

function InvoicesTableHead({
  isCommissionModuleTab,
  showAwaitingPaymentColumns,
  showPaymentDateColumn,
  allVisibleSelected,
  onToggleSelectAll,
  selectAllDisabled = false,
}) {
  return (
    <thead>
      <tr>
        <th className="client-invoices-xero__check-col">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={onToggleSelectAll}
            disabled={selectAllDisabled}
            aria-label="Select all invoices"
          />
        </th>
        <th>
          {isCommissionModuleTab ? "#" : "Number"}
          <FaAngleDown className="client-invoices-xero__sort-icon" />
        </th>
        <th className="cms-commission-invoices-table__client">Client</th>
        <th className="cms-commission-invoices-table__to">To</th>
        <th>Date</th>
        <th>Due date</th>
        {showAwaitingPaymentColumns ? (
          <th className="cms-commission-invoices-table__expected">Expected date</th>
        ) : null}
        <th className="text-end">Amount</th>
        <th className="text-end">Discount</th>
        <th className="text-end">Paid</th>
        {showPaymentDateColumn ? <th>Payment date</th> : null}
        <th className="text-end">Remaining</th>
        {showAwaitingPaymentColumns ? (
          <th className="cms-commission-invoices-table__overdue">Overdue</th>
        ) : null}
        <th>Status</th>
        <th className="client-invoices-xero__actions-col text-end">Actions</th>
      </tr>
    </thead>
  );
}

function InvoicesTableSkeletonBody({ columnCount = 13, rowCount = 10 }) {
  return (
    <tbody aria-busy="true" aria-label="Loading invoice rows">
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <tr key={`skeleton-row-${rowIndex}`}>
          {Array.from({ length: columnCount }).map((__, cellIndex) => (
            <td key={`skeleton-cell-${rowIndex}-${cellIndex}`}>
              <span
                className={`invoice-hub-skeleton__bone invoice-hub-skeleton__bone--cell${
                  cellIndex >= columnCount - 3 ? " is-short" : ""
                }`}
              />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

function readInitialInvoiceListTabs() {
  if (typeof window === "undefined") {
    return { moduleTab: "application", statusTab: "draft" };
  }

  return readFinanceInvoiceListTabsFromLocation({
    pathname: window.location.pathname,
    search: window.location.search,
  });
}

function Invoices() {
  const { permissions: permissionRevision } = usePermissions();
  void permissionRevision;
  const navigate = useNavigate();
  const location = useLocation();
  const initialTabs = useMemo(() => readInitialInvoiceListTabs(), []);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [activeModuleTab, setActiveModuleTab] = useState(() =>
    resolveFinanceInvoicesModuleTab(initialTabs.moduleTab),
  );
  const [activeTab, setActiveTab] = useState(initialTabs.statusTab);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [tabCounts, setTabCounts] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [emailModalTarget, setEmailModalTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [voidingId, setVoidingId] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [followupTarget, setFollowupTarget] = useState(null);
  const [followupUsers, setFollowupUsers] = useState([]);
  const [followupSummaries, setFollowupSummaries] = useState({});
  const [followupSummaryRefreshKey, setFollowupSummaryRefreshKey] = useState(0);
  const [editingExpectedPaymentId, setEditingExpectedPaymentId] = useState(null);
  const prevModuleTabRef = useRef(null);

  const canAppInvoices = canViewApplicationInvoices();
  const canCommInvoices = canViewCommissionInvoices();
  const canAccessInvoicesHub = canAccessFinanceInvoicesHub();

  const allowedModuleTab = useMemo(
    () =>
      resolveFinanceInvoicesModuleTab(activeModuleTab, {
        canApplication: canAppInvoices,
        canCommission: canCommInvoices,
      }),
    [activeModuleTab, canAppInvoices, canCommInvoices],
  );

  const isCommissionModuleTab = allowedModuleTab === "commission";

  const visibleModuleTabs = useMemo(
    () =>
      INVOICE_MODULE_TABS.filter((tab) => {
        if (tab.id === "application") return canAppInvoices;
        if (tab.id === "commission") return canCommInvoices;
        return true;
      }),
    [canAppInvoices, canCommInvoices],
  );

  useEffect(() => {
    if (!canAccessInvoicesHub) return;
    if (allowedModuleTab !== activeModuleTab) {
      setActiveModuleTab(allowedModuleTab);
    }
  }, [allowedModuleTab, activeModuleTab, canAccessInvoicesHub]);

  const showAwaitingPaymentColumns =
    activeTab === "awaiting_payment" || activeTab === "partial";
  const showPaymentDateColumn =
    activeTab === "partial" || activeTab === "paid";
  const tableColumnCount =
    12 + (showAwaitingPaymentColumns ? 2 : 0) + (showPaymentDateColumn ? 1 : 0);

  const canClickAwaitingApprovalTab = canAccessAwaitingApproval();
  const canDeleteInvoice = canDeleteFinanceInvoice();
  const invoiceFollowupPermissions = useMemo(
    () => readInvoiceFollowupPermissions(),
    [],
  );

  const ensureFollowupUsers = useCallback(async () => {
    if (followupUsers.length) return;
    try {
      const catalog = await fetchInvoiceCatalog();
      setFollowupUsers(applyInvoiceCatalog(catalog).followupUsers);
    } catch {
      view_ApiManager(
        "getAllUsers",
        0,
        0,
        (res) => setFollowupUsers(res?.data?.data || []),
        () => setFollowupUsers([]),
      );
    }
  }, [followupUsers.length]);

  useEffect(() => {
    if (!invoices.length) {
      setFollowupSummaries({});
      return undefined;
    }

    const invoiceIds = invoices
      .filter((invoice) => {
        const id = resolveInvoiceId(invoice);
        if (!id) return false;
        return hasInvoiceFollowupPermission(
          resolveListRowFollowupContext(invoice, isCommissionModuleTab),
          invoiceFollowupPermissions,
        );
      })
      .map((invoice) => String(resolveInvoiceId(invoice)));

    if (!invoiceIds.length) {
      setFollowupSummaries({});
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const summaries = await fetchInvoiceFollowupSummariesByInvoices(invoiceIds);
      if (cancelled) return;
      setFollowupSummaries(summaries);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    invoices,
    followupSummaryRefreshKey,
    isCommissionModuleTab,
    invoiceFollowupPermissions,
  ]);

  useEffect(() => {
    if (activeTab === "awaiting_approval" && !canClickAwaitingApprovalTab) {
      setActiveTab("draft");
    }
  }, [activeTab, canClickAwaitingApprovalTab]);

  useEffect(() => {
    setEditingExpectedPaymentId(null);
  }, [activeTab]);

  useEffect(() => {
    if (!canAccessInvoicesHub) return;

    const target = buildFinanceInvoicesReturnUrl({
      statusTab: activeTab,
      moduleTab: allowedModuleTab,
    });
    const currentTabs = readFinanceInvoiceListTabsFromLocation(location);
    const params = new URLSearchParams(location.search || "");
    const currentPath =
      String(location.pathname || "").replace(/\/+$/, "") || "/";
    const hasLegacyModuleQuery =
      params.has("module") ||
      params.has("moduleTab") ||
      params.has("statusTab") ||
      currentPath === "/finance/invoices/application";

    if (
      currentTabs.statusTab === activeTab &&
      currentTabs.moduleTab === allowedModuleTab &&
      params.get("tab") &&
      !hasLegacyModuleQuery
    ) {
      const targetUrl = new URL(target, window.location.origin);
      if (currentPath === (targetUrl.pathname.replace(/\/+$/, "") || "/")) {
        return;
      }
    }

    navigate(target, { replace: true });
  }, [
    activeTab,
    allowedModuleTab,
    canAccessInvoicesHub,
    location.pathname,
    location.search,
    navigate,
  ]);

  const handleExpectedPaymentSaved = (invoiceId, patch = {}) => {
    setInvoices((prev) =>
      prev.map((row) => {
        if (String(resolveInvoiceId(row)) !== String(invoiceId)) return row;
        return mergeExpectedPaymentPatch(row, patch);
      }),
    );
    setEditingExpectedPaymentId(null);
  };

  const fetchTabCounts = async ({
    nextFilters = appliedFilters,
    module = allowedModuleTab,
  } = {}) => {
    if (module === "application" && !canAppInvoices) return;
    if (module === "commission" && !canCommInvoices) return;

    const filterParams = {};
    Object.entries(nextFilters || {}).forEach(([key, value]) => {
      if (key === "status") return;
      const trimmed = String(value || "").trim();
      if (trimmed) filterParams[key] = trimmed;
    });

    try {
      const counts =
        module === "commission"
          ? await fetchCommissionInvoiceCounts(filterParams)
          : await fetchApplicationInvoiceCounts({
              ...filterParams,
              invoice_type: "general",
            });

      const next = {
        all: counts.all ?? 0,
        draft: counts.draft ?? 0,
        awaiting_payment: counts.awaiting_payment ?? 0,
        awaiting_approval: counts.awaiting_approval ?? 0,
        partial: counts.partial ?? 0,
        paid: counts.paid ?? 0,
        void: counts.void ?? 0,
      };
      setTabCounts(next);
    } catch (error) {
      console.error("Failed to load invoice tab counts", error);
    }
  };

  const fetchInvoices = async ({
    nextFilters = appliedFilters,
    nextPagination = pagination,
    module = allowedModuleTab,
    statusTab = activeTab,
  } = {}) => {
    if (module === "application" && !canAppInvoices) {
      setLoading(false);
      setInvoices([]);
      return;
    }
    if (module === "commission" && !canCommInvoices) {
      setLoading(false);
      setInvoices([]);
      return;
    }

    setLoading(true);
    const filtersWithStatus = withInvoiceListStatus(nextFilters, statusTab);

    try {
      if (module === "commission") {
        const { items, pagination: pageMeta } = await listAllCommissionInvoices(
          buildCommissionInvoiceListParams(filtersWithStatus, nextPagination),
        );
        setInvoices(items);
        if (pageMeta) {
          setPagination(pageMeta);
        } else {
          setPagination({
            currentPage: nextPagination.currentPage || 1,
            lastPage: 1,
            perPage: nextPagination.perPage || 15,
            total: items.length,
            from: items.length ? 1 : 0,
            to: items.length,
          });
        }
        return;
      }

      const invoiceResponse = await apiUrl.get("finance-invoices", {
        params: buildFinanceInvoiceParams(filtersWithStatus, nextPagination),
      });

      if (isFinanceInvoiceApiSuccess(invoiceResponse)) {
        setInvoices(normalizeFinanceInvoiceList(invoiceResponse));
        const pageMeta = normalizeFinanceInvoicePagination(invoiceResponse);
        if (pageMeta) {
          setPagination(pageMeta);
        } else {
          const list = normalizeFinanceInvoiceList(invoiceResponse);
          setPagination({
            currentPage: nextPagination.currentPage || 1,
            lastPage: 1,
            perPage: nextPagination.perPage || 15,
            total: list.length,
            from: list.length ? 1 : 0,
            to: list.length,
          });
        }
      } else {
        setInvoices([]);
        setPagination({
          ...DEFAULT_PAGINATION,
          perPage: nextPagination.perPage || DEFAULT_PAGINATION.perPage,
        });
      }
    } catch (error) {
      console.error("Failed to load invoices", error);
      setInvoices([]);
      setPagination({
        ...DEFAULT_PAGINATION,
        perPage: nextPagination.perPage || DEFAULT_PAGINATION.perPage,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const nextPagination = {
      ...DEFAULT_PAGINATION,
      perPage: pagination.perPage || DEFAULT_PAGINATION.perPage,
    };
    const isModuleChange =
      prevModuleTabRef.current !== null &&
      prevModuleTabRef.current !== allowedModuleTab;
    prevModuleTabRef.current = allowedModuleTab;

    if (!canAccessInvoicesHub) return;

    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setSelectedIds([]);
    setPagination(nextPagination);

    if (isModuleChange) {
      setActiveTab("draft");
      fetchInvoices({
        nextFilters: EMPTY_FILTERS,
        nextPagination,
        module: allowedModuleTab,
        statusTab: "draft",
      });
    } else {
      fetchInvoices({
        nextFilters: EMPTY_FILTERS,
        nextPagination,
        module: allowedModuleTab,
        statusTab: activeTab,
      });
    }

    fetchTabCounts({
      nextFilters: EMPTY_FILTERS,
      module: allowedModuleTab,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedModuleTab, canAccessInvoicesHub]);

  const beginInvoiceListTransition = () => {
    setLoading(true);
    setInvoices([]);
    setSelectedIds([]);
  };

  const handleModuleTabChange = (tabId) => {
    if (tabId === activeModuleTab) return;
    if (tabId === "application" && !canAppInvoices) return;
    if (tabId === "commission" && !canCommInvoices) return;
    beginInvoiceListTransition();
    setActiveModuleTab(tabId);
    setActiveTab("draft");
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    const nextPagination = { ...pagination, currentPage: 1 };
    const nextStatusTab =
      filters.status === "overdue"
        ? "awaiting_payment"
        : filters.status || "draft";
    beginInvoiceListTransition();
    setAppliedFilters(filters);
    setActiveTab(nextStatusTab);
    setSelectedIds([]);
    setPagination(nextPagination);
    fetchInvoices({
      nextFilters: filters,
      nextPagination,
      statusTab: nextStatusTab,
    });
    fetchTabCounts({ nextFilters: filters });
  };

  const handleClearAll = () => {
    const nextPagination = { ...DEFAULT_PAGINATION, perPage: pagination.perPage };
    beginInvoiceListTransition();
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setActiveTab("draft");
    setSelectedIds([]);
    setPagination(nextPagination);
    fetchInvoices({
      nextFilters: EMPTY_FILTERS,
      nextPagination,
      statusTab: "draft",
    });
    fetchTabCounts({ nextFilters: EMPTY_FILTERS });
  };

  const handleStatusTabChange = (tabId) => {
    if (tabId === activeTab) return;

    beginInvoiceListTransition();

    const nextFilters = { ...appliedFilters, status: "" };
    const nextPagination = { ...pagination, currentPage: 1 };

    setFilters((prev) => ({ ...prev, status: "" }));
    setAppliedFilters(nextFilters);
    setActiveTab(tabId);
    setSelectedIds([]);
    setPagination(nextPagination);
    fetchInvoices({
      nextFilters,
      nextPagination,
      statusTab: tabId,
    });
  };

  const handlePageClick = (event) => {
    const nextPage = (event?.selected || 0) + 1;
    if (nextPage === pagination.currentPage) return;
    const nextPagination = { ...pagination, currentPage: nextPage };
    setSelectedIds([]);
    setPagination(nextPagination);
    fetchInvoices({ nextPagination, statusTab: activeTab });
  };

  const handlePerPageChange = (event) => {
    const nextPerPage = Number(event.target.value) || 15;
    const nextPagination = {
      ...pagination,
      currentPage: 1,
      perPage: nextPerPage,
    };
    setPagination(nextPagination);
    fetchInvoices({ nextPagination, statusTab: activeTab });
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const isCommission = isCommissionModuleInvoice(invoice);
      if (isCommissionModuleTab) return isCommission;
      return !isCommission;
    });
  }, [invoices, isCommissionModuleTab]);

  const selectedInvoices = useMemo(
    () =>
      filteredInvoices.filter((invoice) =>
        selectedIds.includes(String(resolveInvoiceId(invoice))),
      ),
    [filteredInvoices, selectedIds],
  );

  const hasSelection = selectedIds.length > 0;
  const selectedCount = selectedInvoices.length || selectedIds.length;
  const canEmailSelectedInvoices = EMAIL_ENABLED_STATUS_TABS.has(activeTab);
  const selectedInvoiceIds = useMemo(() => {
    const idsFromRows = selectedInvoices
      .map((invoice) => resolveInvoiceId(invoice))
      .filter(Boolean)
      .map(String);
    if (idsFromRows.length) return idsFromRows;
    return selectedIds.filter(Boolean);
  }, [selectedInvoices, selectedIds]);

  const handlePreviewSelected = () => {
    if (!selectedInvoiceIds.length) return;
    setShowPreviewModal(true);
  };

  const handleEmailSelected = () => {
    if (!selectedInvoiceIds.length || !canEmailSelectedInvoices) {
      return;
    }

    const groupKeys = [
      ...new Set(
        selectedInvoices
          .map((invoice) =>
            resolveBulkEmailGroupKey(invoice, isCommissionModuleTab),
          )
          .filter(Boolean),
      ),
    ];

    if (groupKeys.length !== 1) {
      Swal.fire(
        isCommissionModuleTab ? "Select one provider" : "Select one client",
        isCommissionModuleTab
          ? "Email can only be sent when all selected commission invoices belong to the same provider."
          : "Email can only be sent when all selected invoices belong to the same client.",
        "warning",
      );
      return;
    }

    const firstInvoice = selectedInvoices[0];
    const clientId = resolveClientId(firstInvoice);

    setEmailModalTarget({
      invoiceIds: selectedInvoiceIds,
      clientId,
      isCommission: isCommissionModuleTab,
      fallbackRecipientEmail: isCommissionModuleTab
        ? ""
        : resolveClientEmail(firstInvoice),
      fallbackRecipientName: isCommissionModuleTab
        ? resolveProviderName(firstInvoice)
        : resolveClientName(firstInvoice),
      fallbackInvoiceNumber:
        selectedInvoiceIds.length === 1
          ? resolveInvoiceNumber(firstInvoice)
          : "",
    });
  };

  const handleVoidInvoice = async (invoice) => {
    const invoiceId = resolveInvoiceId(invoice);
    if (!invoiceId) return;

    const confirmed = await Swal.fire({
      title: "Void invoice?",
      text: `Move invoice ${resolveInvoiceNumber(invoice)} to Void? The record will be kept for reference.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Void",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#64748b",
    });

    if (!confirmed.isConfirmed) return;

    const isCommission =
      isCommissionModuleTab || isCommissionModuleInvoice(invoice);

    try {
      setVoidingId(String(invoiceId));
      if (isCommission) {
        await voidCommissionInvoice(invoiceId);
      } else {
        await voidFinanceInvoice(invoiceId);
      }

      await Swal.fire({
        icon: "success",
        title: "Voided",
        text: "Invoice moved to Void.",
        timer: 1400,
        showConfirmButton: false,
      });

      setActiveTab("void");
      fetchInvoices({ statusTab: "void" });
      fetchTabCounts();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to void invoice.",
        "error",
      );
    } finally {
      setVoidingId(null);
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!canDeleteInvoice) return;
    const invoiceId = resolveInvoiceId(invoice);
    if (!invoiceId) return;

    const confirmed = await Swal.fire({
      title: "Delete invoice?",
      text: `Delete invoice ${resolveInvoiceNumber(invoice)}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!confirmed.isConfirmed) return;

    try {
      setDeletingId(String(invoiceId));
      if (isCommissionModuleTab || isCommissionModuleInvoice(invoice)) {
        await deleteCommissionInvoice(invoiceId);
      } else {
        const response = await apiUrl.delete(`finance-invoices/${invoiceId}/delete`);
        if (!isFinanceInvoiceApiSuccess(response)) {
          throw new Error(
            getFinanceInvoiceApiMessage(response, "Failed to delete invoice."),
          );
        }
      }

      setInvoices((prev) =>
        prev.filter((row) => String(resolveInvoiceId(row)) !== String(invoiceId)),
      );
      setSelectedIds((prev) =>
        prev.filter((item) => item !== String(invoiceId)),
      );

      await Swal.fire(
        "Deleted",
        isCommissionModuleTab || isCommissionModuleInvoice(invoice)
          ? "Commission invoice deleted successfully."
          : "Invoice deleted successfully.",
        "success",
      );
      fetchInvoices({ statusTab: activeTab });
      fetchTabCounts();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to delete invoice.",
        "error",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const summaryCurrency =
    filteredInvoices[0]?.currency || invoices[0]?.currency || "AUD";
  const summaryTotal = filteredInvoices.reduce(
    (sum, invoice) =>
      sum + Number(invoice?.amount_due ?? invoice?.amountDue ?? 0),
    0,
  );

  const allVisibleSelected =
    filteredInvoices.length > 0 &&
    filteredInvoices.every((invoice) =>
      selectedIds.includes(String(resolveInvoiceId(invoice))),
    );

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(
        filteredInvoices.map((invoice) => String(resolveInvoiceId(invoice))),
      );
      setSelectedIds((prev) => prev.filter((item) => !visibleIds.has(item)));
      return;
    }

    const nextIds = new Set(selectedIds);
    filteredInvoices.forEach((invoice) => {
      nextIds.add(String(resolveInvoiceId(invoice)));
    });
    setSelectedIds([...nextIds]);
  };

  const toggleRow = (invoiceId) => {
    const key = String(invoiceId);
    setSelectedIds((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
  };

  if (!canAccessInvoicesHub) {
    return (
      <div className="client-invoices-xero p-4">
        <p className="text-muted mb-0">
          You do not have permission to view application or commission invoices.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="client-invoices-xero">
        <div className="client-invoices-xero__breadcrumb">
          <span>Manage Finance</span>
          <span aria-hidden="true">›</span>
          <span>Invoices</span>
        </div>

        <div className="client-invoices-xero__page-head">
          <h1 className="client-invoices-xero__title">Invoices</h1>
          <p className="client-invoices-xero__subtitle">
            {canAppInvoices && canCommInvoices
              ? "Manage application billing and commission invoices in one place."
              : canCommInvoices
                ? "Provider commission billing."
                : "Client application fees and finance invoices."}
          </p>
        </div>

        {visibleModuleTabs.length > 1 ? (
        <nav
          className="client-invoices-xero__module-switch"
          role="tablist"
          aria-label="Invoice type"
        >
          {visibleModuleTabs.map((tab) => {
            const isActive = allowedModuleTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`client-invoices-xero__module-switch-item${
                  isActive ? " is-active" : ""
                }`}
                onClick={() => handleModuleTabChange(tab.id)}
              >
                <span className="client-invoices-xero__module-switch-icon">
                  <Icon aria-hidden="true" />
                </span>
                <span className="client-invoices-xero__module-switch-copy">
                  <span className="client-invoices-xero__module-switch-label">
                    {tab.label}
                    {tab.badge ? (
                      <span className="client-invoices-xero__module-switch-badge">
                        {tab.badge}
                      </span>
                    ) : null}
                  </span>
                  <span className="client-invoices-xero__module-switch-subtitle">
                    {tab.subtitle}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>
        ) : null}

        <div className="client-invoices-xero__actions">
          {canAppInvoices && !isCommissionModuleTab ? (
          <Link
            to={buildFinanceCreateInvoicePath({
              moduleTab: allowedModuleTab,
              statusTab: activeTab,
            })}
            className="client-invoices-xero__btn client-invoices-xero__btn--primary"
          >
            New invoice
          </Link>
          ) : null}
        </div>

        <div className="client-invoices-xero__filters">
          <div className="client-invoices-xero__filters-grid">
            <div className="client-invoices-xero__filter-field">
              <label htmlFor="finance-invoice-filter-name">
                Name or client/user/invoice ID
              </label>
              <input
                id="finance-invoice-filter-name"
                type="text"
                className="client-invoices-xero__filter-input"
                placeholder="Name or client/user/invoice ID"
                value={filters.name}
                onChange={(e) => handleFilterChange("name", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
            </div>

            <div className="client-invoices-xero__filter-field">
              <label htmlFor="finance-invoice-filter-email">Email</label>
              <input
                id="finance-invoice-filter-email"
                type="text"
                className="client-invoices-xero__filter-input"
                placeholder="Email"
                value={filters.email}
                onChange={(e) => handleFilterChange("email", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
            </div>

            <div className="client-invoices-xero__filter-field">
              <label htmlFor="finance-invoice-filter-status">Status</label>
              <select
                id="finance-invoice-filter-status"
                className="client-invoices-xero__filter-input"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All statuses</option>
                {INVOICE_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="client-invoices-xero__filter-field">
              <label htmlFor="finance-invoice-filter-number">
                Invoice number
              </label>
              <input
                id="finance-invoice-filter-number"
                type="text"
                className="client-invoices-xero__filter-input"
                placeholder="Invoice number"
                value={filters.invoice_number}
                onChange={(e) =>
                  handleFilterChange("invoice_number", e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
            </div>
          </div>

          <div className="client-invoices-xero__filters-actions">
            <button
              type="button"
              className="client-invoices-xero__btn client-invoices-xero__btn--primary"
              onClick={handleSearch}
              disabled={loading}
            >
              <FaSearch className="me-1" />
              Search
            </button>
            <button
              type="button"
              className="client-invoices-xero__btn"
              onClick={handleClearAll}
              disabled={loading}
            >
              Clear all
            </button>
          </div>
        </div>

        <div
          className="client-invoices-xero__tabs"
          role="tablist"
          aria-label="Invoice status"
        >
          {STATUS_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const count = tabCounts[tab.id] ?? 0;
            const isAwaitingApprovalTab = tab.id === "awaiting_approval";
            const isTabDisabled =
              isAwaitingApprovalTab && !canClickAwaitingApprovalTab;

            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-disabled={isTabDisabled}
                disabled={isTabDisabled}
                title={
                  isTabDisabled
                    ? "You do not have permission to view Awaiting Approval invoices"
                    : undefined
                }
                className={`client-invoices-xero__tab${
                  isActive ? " is-active" : ""
                }${isTabDisabled ? " is-disabled" : ""}`}
                onClick={() => {
                  if (isTabDisabled) return;
                  handleStatusTabChange(tab.id);
                }}
              >
                {tab.label}
                {` (${count})`}
              </button>
            );
          })}
        </div>

        <div className="client-invoices-xero__toolbar">
          <div className="client-invoices-xero__toolbar-left">
            <button
              type="button"
              className="client-invoices-xero__tool-btn"
              disabled={!hasSelection}
              onClick={handlePreviewSelected}
            >
              Preview
            </button>
            <button
              type="button"
              className="client-invoices-xero__tool-btn"
              disabled={!hasSelection || !canEmailSelectedInvoices}
              title={
                !canEmailSelectedInvoices
                  ? "Email is not available on the Awaiting Approval tab"
                  : !hasSelection
                    ? "Select one or more invoices"
                    : undefined
              }
              onClick={handleEmailSelected}
            >
              Email
            </button>
            <span className="client-invoices-xero__selection-label">
              {hasSelection
                ? `${selectedCount} item${selectedCount === 1 ? "" : "s"} selected`
                : "No items selected"}
            </span>
          </div>

          <div className="client-invoices-xero__toolbar-right">
            <span className="client-invoices-xero__summary">
              {loading ? (
                "Loading..."
              ) : (
                <>
                  {filteredInvoices.length} item
                  {filteredInvoices.length === 1 ? "" : "s"} |{" "}
                  {formatMoney(summaryTotal, summaryCurrency)}
                </>
              )}
            </span>
          </div>
        </div>

        <div className="client-invoices-xero__table-card">
          {!loading && filteredInvoices.length === 0 ? (
            <div className="client-invoices-xero__empty">
              <p className="mb-0">No invoices found.</p>
            </div>
          ) : (
            <div
              className={`cms-commissions-table-wrap table-responsive cms-commission-invoices-table-wrap${
                loading ? " invoice-hub-skeleton" : ""
              }`}
            >
              <div className="invoice-hub-skeleton__table-wrap">
                <table className="client-invoices-xero__table cms-commission-invoices-table">
                  <InvoicesTableHead
                    isCommissionModuleTab={isCommissionModuleTab}
                    showAwaitingPaymentColumns={showAwaitingPaymentColumns}
                    showPaymentDateColumn={showPaymentDateColumn}
                    allVisibleSelected={allVisibleSelected}
                    onToggleSelectAll={toggleSelectAll}
                    selectAllDisabled={loading}
                  />
                  {loading ? (
                    <InvoicesTableSkeletonBody
                      columnCount={tableColumnCount}
                      rowCount={pagination.perPage || DEFAULT_PAGINATION.perPage}
                    />
                  ) : (
                <tbody>
                  {filteredInvoices.map((invoice, index) => {
                    const invoiceId = resolveInvoiceId(invoice);
                    const clientId = resolveClientId(invoice);
                    const invoiceKey = invoiceId || `invoice-${index}`;
                    const status = resolveInvoiceStatus(invoice);
                    const statusDisplay = resolveInvoiceStatusDisplay(invoice);
                    const isRowBusy =
                      deletingId === String(invoiceId) ||
                      voidingId === String(invoiceId);
                    const canVoid = activeTab === "awaiting_payment";
                    const currency = invoice?.currency || summaryCurrency;
                    const amount = resolveInvoiceTableAmount(invoice);
                    const discount = resolveInvoiceDiscount(invoice);
                    const paidAmount = Number(
                      invoice?.paid_amount ?? invoice?.paidAmount ?? 0,
                    );
                    const remainingAmount = Number(
                      invoice?.amount_due ?? invoice?.amountDue ?? 0,
                    );
                    const issueDate = invoice?.issue_date || invoice?.issueDate;
                    const dueDate = invoice?.due_date || invoice?.dueDate;
                    const paymentDate = resolveInvoiceLatestPaymentDate(invoice);
                    const clientName = resolveClientName(invoice);
                    const contactName = isCommissionModuleTab
                      ? resolveProviderName(invoice)
                      : clientName;
                    const expectedPaymentInvoice = toExpectedPaymentInvoiceShape(invoice);
                    const overdueDisplay = resolveOverdueDisplay(invoice);
                    const isEditingExpected =
                      editingExpectedPaymentId != null &&
                      String(editingExpectedPaymentId) === String(invoiceId);

                    return (
                      <React.Fragment key={invoiceKey}>
                      <tr
                        className={
                          selectedIds.includes(String(invoiceId))
                            ? "is-selected"
                            : ""
                        }
                      >
                        <td className="client-invoices-xero__check-col">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(String(invoiceId))}
                            onChange={() => toggleRow(invoiceId)}
                            aria-label={`Select invoice ${resolveInvoiceNumber(
                              invoice,
                            )}`}
                          />
                        </td>
                        <td>
                          {isCommissionModuleTab ? (
                            invoiceId && clientId ? (
                              <Link
                                className="client-invoices-xero__invoice-link"
                                to={buildCommissionHubEditInvoicePath(
                                  clientId,
                                  invoiceId,
                                  {
                                    moduleTab: allowedModuleTab,
                                    statusTab: activeTab,
                                  },
                                )}
                                state={{
                                  returnTo: buildFinanceInvoicesReturnUrl({
                                    moduleTab: allowedModuleTab,
                                    statusTab: activeTab,
                                  }),
                                }}
                              >
                                {resolveInvoiceNumber(invoice)}
                              </Link>
                            ) : (
                              resolveInvoiceNumber(invoice)
                            )
                          ) : (
                            <div
                              className={`cms-invoice-number-cell${
                                resolveCommissionTypeBadge(invoice)
                                  ? " cms-invoice-number-cell--tagged"
                                  : ""
                              }`}
                            >
                              {resolveCommissionTypeBadge(invoice) ? (
                                <span
                                  className={`cms-invoice-module-badge cms-invoice-number-cell__badge ${
                                    resolveCommissionTypeBadge(invoice) ===
                                    "Commission + Bonus"
                                      ? "cms-invoice-module-badge--mixed"
                                      : resolveCommissionTypeBadge(invoice) ===
                                          "Bonus"
                                        ? "cms-invoice-module-badge--bonus"
                                        : "cms-invoice-module-badge--commission"
                                  }`}
                                >
                                  {resolveCommissionTypeBadge(invoice)}
                                </span>
                              ) : null}
                              <div className="cms-invoice-number-cell__number">
                                {invoiceId && clientId ? (
                                  <Link
                                    className="client-invoices-xero__invoice-link"
                                    to={buildFinanceEditInvoicePath(
                                      clientId,
                                      invoiceId,
                                      {
                                        moduleTab: allowedModuleTab,
                                        statusTab: activeTab,
                                      },
                                    )}
                                  >
                                    {resolveInvoiceNumber(invoice)}
                                  </Link>
                                ) : (
                                  resolveInvoiceNumber(invoice)
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        {renderInvoiceContactCell(clientName, {
                          linkTo: clientId
                            ? `/clients/view-client-detail/${clientId}`
                            : null,
                          linkClassName:
                            "client-invoices-xero__contact-link cms-commission-invoices-table__contact-name",
                          cellClassName:
                            "cms-commission-invoices-table__contact-cell cms-commission-invoices-table__client",
                        })}
                        {renderInvoiceContactCell(contactName, {
                          linkTo:
                            !isCommissionModuleTab && clientId
                              ? `/clients/view-client-detail/${clientId}`
                              : null,
                          linkClassName:
                            "client-invoices-xero__contact-link cms-commission-invoices-table__contact-name",
                          cellClassName:
                            "cms-commission-invoices-table__contact-cell cms-commission-invoices-table__to",
                        })}
                        <td>
                          {issueDate
                            ? moment(issueDate).format("MMM D, YYYY")
                            : ""}
                        </td>
                        <td>
                          {dueDate ? moment(dueDate).format("MMM D, YYYY") : ""}
                        </td>
                        {showAwaitingPaymentColumns ? (
                          <td className="cms-commission-invoices-table__expected">
                            <CommissionInvoiceExpectedPaymentCell
                              invoice={expectedPaymentInvoice}
                                disabled={loading || isRowBusy}
                              onStartEdit={() =>
                                setEditingExpectedPaymentId(invoiceId)
                              }
                            />
                          </td>
                        ) : null}
                        <td className="text-end fw-semibold">
                          {formatMoney(amount, currency)}
                        </td>
                        <td className="text-end client-invoices-xero__discount">
                          {discount > 0
                            ? `-${formatMoney(discount, currency)}`
                            : formatMoney(0, currency)}
                        </td>
                        <td className="text-end">
                          {formatMoney(paidAmount, currency)}
                        </td>
                        {showPaymentDateColumn ? (
                          <td>
                            {paymentDate
                              ? moment(paymentDate).format("MMM D, YYYY")
                              : "—"}
                          </td>
                        ) : null}
                        <td className="text-end">
                          {formatMoney(remainingAmount, currency)}
                        </td>
                        {showAwaitingPaymentColumns ? (
                          <td className="cms-commission-invoices-table__overdue">
                            {overdueDisplay.isOverdue ? (
                              <span
                                className="cms-commission-invoices-table__overdue-badge"
                                title={`Past due by ${overdueDisplay.label}`}
                              >
                                {overdueDisplay.label}
                              </span>
                            ) : (
                              <span className="cms-commission-invoices-table__overdue-none">
                                —
                              </span>
                            )}
                          </td>
                        ) : null}
                        <td>
                          <span className="client-invoices-xero__status">
                            {getInvoiceStatusLabel(statusDisplay)}
                          </span>
                        </td>
                        <td className="text-end client-invoices-xero__actions-cell">
                          <div className="client-invoices-xero__row-actions">
                            {canQuickPayInvoice(invoice, resolveInvoiceStatus) ? (
                              <button
                                type="button"
                                className="client-invoices-xero__icon-action client-invoices-xero__icon-action--payment"
                                title="Add payment"
                                aria-label={`Add payment for invoice ${resolveInvoiceNumber(
                                  invoice,
                                )}`}
                                disabled={!invoiceId}
                                onClick={() =>
                                  setPaymentTarget({
                                    invoiceId,
                                    clientId: resolveClientId(invoice),
                                    invoiceNumber: resolveInvoiceNumber(invoice),
                                    isCommission:
                                      isCommissionModuleTab ||
                                      isCommissionModuleInvoice(invoice),
                                    initialAmountDue: remainingAmount,
                                  })
                                }
                              >
                                <FaMoneyBillWave size={13} />
                              </button>
                            ) : null}
                            {canVoid ? (
                              <button
                                type="button"
                                className="client-invoices-xero__icon-action"
                                title="Void invoice"
                                aria-label={`Void invoice ${resolveInvoiceNumber(
                                  invoice,
                                )}`}
                                disabled={isRowBusy || !invoiceId}
                                onClick={() => handleVoidInvoice(invoice)}
                                style={{
                                  color: "#64748b",
                                  borderColor: "#cbd5e1",
                                }}
                              >
                                <FaBan size={13} />
                              </button>
                            ) : null}
                            {hasInvoiceFollowupPermission(
                              resolveListRowFollowupContext(
                                invoice,
                                isCommissionModuleTab,
                              ),
                              invoiceFollowupPermissions,
                            ) && invoiceId ? (
                              <InvoiceFollowupListBell
                                count={
                                  followupSummaries[String(invoiceId)]?.total || 0
                                }
                                summary={
                                  followupSummaries[String(invoiceId)] || null
                                }
                                disabled={isRowBusy}
                                ariaLabel={`Follow-ups for invoice ${resolveInvoiceNumber(
                                  invoice,
                                )}`}
                                onClick={() => {
                                  ensureFollowupUsers();
                                  setFollowupTarget({
                                    financeInvoiceId: Number(invoiceId),
                                    contextType: resolveListRowFollowupContext(
                                      invoice,
                                      isCommissionModuleTab,
                                    ),
                                    clientId: clientId
                                      ? Number(clientId)
                                      : null,
                                    providerId: resolveProviderId(invoice)
                                      ? Number(resolveProviderId(invoice))
                                      : null,
                                    commissionPlanId: resolveCommissionPlanId(
                                      invoice,
                                    )
                                      ? Number(
                                          resolveCommissionPlanId(invoice),
                                        )
                                      : null,
                                    applicationId:
                                      invoice?.application_id ??
                                      invoice?.applicationId ??
                                      null,
                                    applicationCourseId:
                                      invoice?.application_course_id ??
                                      invoice?.applicationCourseId ??
                                      null,
                                  });
                                }}
                              />
                            ) : null}
                            <button
                              type="button"
                              className="client-invoices-xero__icon-action client-invoices-xero__icon-action--danger"
                              title={
                                canDeleteInvoice
                                  ? "Delete invoice"
                                  : "You do not have permission to delete invoices"
                              }
                              aria-label={`Delete invoice ${resolveInvoiceNumber(
                                invoice,
                              )}`}
                              aria-disabled={!canDeleteInvoice}
                              disabled={
                                !canDeleteInvoice ||
                                isRowBusy
                              }
                              onClick={() => {
                                if (!canDeleteInvoice) return;
                                handleDeleteInvoice(invoice);
                              }}
                            >
                              <FaTrashAlt size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {showAwaitingPaymentColumns && isEditingExpected ? (
                        <tr className="cms-invoice-expected-payment-row">
                          <td colSpan={tableColumnCount}>
                            <CommissionInvoiceExpectedPaymentEditor
                              inline
                              invoiceId={invoiceId}
                              expectedPaymentDate={
                                expectedPaymentInvoice.expectedPaymentDate
                              }
                              expectedPaymentNote={
                                expectedPaymentInvoice.expectedPaymentNote
                              }
                              saveExpectedPayment={resolveExpectedPaymentSaveHandler(
                                invoice,
                              )}
                              onCancel={() => setEditingExpectedPaymentId(null)}
                              onSaved={(patch) =>
                                handleExpectedPaymentSaved(invoiceId, patch)
                              }
                            />
                          </td>
                        </tr>
                      ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                  )}
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="client-invoices-xero__pagination row align-items-center mt-3 mb-1">
          <div className="col-auto">
            <select
              value={pagination.perPage}
              onChange={handlePerPageChange}
              className="form-select form-select-sm"
              style={{ minWidth: 80 }}
              disabled={loading}
              aria-label="Rows per page"
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="col-auto text-muted small">
            {pagination.from && pagination.to
              ? `${pagination.from}-${pagination.to} of ${pagination.total}`
              : `${pagination.total} entries`}{" "}
            | Page {pagination.currentPage} of {pagination.lastPage || 1}
          </div>
          <div className="col">
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              pageCount={pagination.lastPage || 1}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              forcePage={Math.max(pagination.currentPage - 1, 0)}
              containerClassName="pagination justify-content-end mb-0"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              breakClassName="page-item"
              breakLinkClassName="page-link"
              activeClassName="active"
              disabledClassName="disabled"
            />
          </div>
        </div>
      </div>

      <ClientInvoicesPrintModal
        show={showPreviewModal}
        invoiceIds={selectedInvoiceIds}
        clientId={
          selectedInvoices[0] ? resolveClientId(selectedInvoices[0]) : null
        }
        isCommission={isCommissionModuleTab}
        onClose={() => setShowPreviewModal(false)}
      />

      {emailModalTarget ? (
        <FinanceInvoiceApproveEmailModal
          open={Boolean(emailModalTarget)}
          invoiceIds={emailModalTarget?.invoiceIds || []}
          clientId={emailModalTarget?.clientId}
          isCommission={Boolean(emailModalTarget?.isCommission)}
          fallbackRecipientEmail={emailModalTarget?.fallbackRecipientEmail || ""}
          fallbackRecipientName={emailModalTarget?.fallbackRecipientName || ""}
          fallbackInvoiceNumber={emailModalTarget?.fallbackInvoiceNumber || ""}
          onClose={() => setEmailModalTarget(null)}
          onSent={() => {
            setSelectedIds([]);
            fetchInvoices({ statusTab: activeTab });
            fetchTabCounts();
          }}
        />
      ) : null}

      <FinanceInvoiceQuickPayment
        show={Boolean(paymentTarget)}
        invoiceId={paymentTarget?.invoiceId || null}
        clientId={paymentTarget?.clientId || null}
        invoiceNumber={paymentTarget?.invoiceNumber || ""}
        isCommission={Boolean(paymentTarget?.isCommission)}
        initialAmountDue={paymentTarget?.initialAmountDue}
        onClose={() => setPaymentTarget(null)}
        onSuccess={() => {
          fetchInvoices({ statusTab: activeTab });
          fetchTabCounts();
        }}
      />

      <InvoiceFollowupModal
        show={Boolean(followupTarget)}
        contextType={
          followupTarget?.contextType || CONTEXT_INVOICE_COMMISSION
        }
        financeInvoiceId={followupTarget?.financeInvoiceId ?? null}
        commissionPlanId={followupTarget?.commissionPlanId ?? null}
        clientId={followupTarget?.clientId ?? null}
        providerId={followupTarget?.providerId ?? null}
        applicationId={followupTarget?.applicationId ?? null}
        applicationCourseId={followupTarget?.applicationCourseId ?? null}
        users={followupUsers}
        onClose={() => setFollowupTarget(null)}
        onSuccess={() => {
          setFollowupTarget(null);
          setFollowupSummaryRefreshKey((key) => key + 1);
        }}
      />
    </>
  );
}

export default Invoices;
